import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {QueryError, ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {IDPInfo, SAMLSettings} from "@/lib/types/statuspages.ts";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import {SAMLTesting} from "./SAMLTesting.tsx";
import {SSOSetupStep1} from "./SSOSetupStep1.tsx";
import {SSOFormSchema, SSOFormValues, SSOSetupStep2} from "./SSOSetupStep2.tsx";

import styles from "./SSOSetup.module.scss";

interface SSOSetupProps {
  metadataURL: string;
  samlSettings: SAMLSettings;
  testIDPs: IDPInfo[];
  updateSAML: (data: SSOFormValues) => Promise<{status: ResponseStatus} | QueryError | undefined>;
  removeSAML: () => Promise<boolean>;
  variant?: "general" | "tab";
}

export const SSOSetup = ({
  metadataURL,
  samlSettings,
  testIDPs,
  updateSAML,
  removeSAML,
  variant = "general",
}: SSOSetupProps) => {
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ServerErrors | undefined>({});
  const methods = useForm<SSOFormValues>({
    resolver: yupResolver<SSOFormValues>(SSOFormSchema),
    defaultValues: {
      idp_entity_id: samlSettings.idp_entity_id,
      idp_sso_target_url: samlSettings.idp_sso_target_url,
      idp_certificate: samlSettings.idp_certificate,
      use_sha1_for_signature: samlSettings.use_sha1_for_signature,
      unsigned_authn_requests: samlSettings.unsigned_authn_requests,
    },
  });

  const {handleSubmit, reset} = methods;

  useEffect(() => {
    if (validationErrors) {
      setServerErrors(validationErrors, methods.setError, "");
    }
  }, [validationErrors, methods.setError]);
  const onSubmit = async (data: SSOFormValues) => {
    const result = await updateSAML(data);
    setValidationErrors({});
    if (result?.status === ResponseStatus.SUCCESS) {
      toast.success("SAML Settings have been updated");
    } else if (result?.status === ResponseStatus.ERROR) {
      const errorMessages = result as {message: {[key: string]: string[]}};
      if (errorMessages) {
        const errors: {[key: string]: string[]} = {};
        Object.entries(errorMessages.message).forEach(([field, messages]) => {
          errors[field] = messages;
        });
        setValidationErrors(errors as ServerErrors);
        toast.error("Some form fields need attention", {id: "SAML-form-errors"});
      }
    } else {
      toast.error("Oops.. something went wrong. Please contact our support if issue persists");
    }
  };
  const handleRemoveSAMLSettings = async () => {
    const result = await removeSAML();
    if (result) {
      toast.success("SAML Settings have been cleared");
      reset({
        idp_entity_id: "",
        idp_sso_target_url: "",
        idp_certificate: "",
        use_sha1_for_signature: false,
        unsigned_authn_requests: false,
      });
    } else {
      toast.error("Oops.. something went wrong. Please contact our support if issue persists");
    }
    setShowConfirm(false);
  };

  return (
    <div className={classNames("row", variant ? styles[variant] : "")}>
      <ActionDialog
        show={showConfirm}
        title="Disable SSO"
        onConfirm={handleRemoveSAMLSettings}
        onCancel={() => setShowConfirm(false)}
        cancelBtnText="Cancel"
        confirmBtnText="Disable"
        confirmBtnVariant="red"
      >
        Are you sure you want to remove your SAML SSO integration?
        <Alert className={"mt-4"} variant={"maintenance"} size={"large"} header={"Warning"}>
          Users will no longer be required to log in via SSO
        </Alert>
      </ActionDialog>
      <FormProvider {...methods}>
        <div className={"col" + (testIDPs.length > 0 ? "-lg-8" : "")}>
          <SSOSetupStep1 metadataURL={metadataURL} samlSettings={samlSettings} variant={variant} />
          <hr />
          <form onSubmit={handleSubmit(onSubmit, console.error)} className={styles.form}>
            <SSOSetupStep2 className="mt-3" />
            <Divider className="my-4" />
            <div className="row mt-3">
              <div className={classNames("d-flex gap-3", {"justify-content-between": variant === "general"})}>
                <Button variant={"secondary"} onClick={() => setShowConfirm(true)}>
                  Disable SAML SSO
                </Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </div>
          </form>
        </div>
        {testIDPs.length > 0 && (
          <div className="col-lg-3 offset-lg-1">
            <hr className="d-lg-none mt-4" />
            <SAMLTesting idps={testIDPs} wayflessURL={samlSettings.sp_wayfless_url} />
          </div>
        )}
      </FormProvider>
    </div>
  );
};
